import * as React from 'react'

import Form, { FormGroup, FormSection } from 'main/components/common/Form'
import { ExternalSchool_externalSchool as IExternalSchool } from 'main/gql/externalSchool'
import { SLACK_NOTIFICATION_CHANNEL } from 'main/pages/ExternalSchool'
import { Button, Link, PhoneTextField, spacing, Text, TextArea, TextField } from 'main/ui'
import { formatAddress } from 'main/util/address'
import { emailInputIsValid } from 'main/util/email'
import { phoneInputIsValid } from 'main/util/phone'
import segment from 'util/analytics/segment'

export interface IParams {
  email: string
  message: string
  phone: string
}

interface IProps {
  externalSchool: IExternalSchool
  onComplete: () => void
}

export default function RemoveForm({ externalSchool, onComplete }: IProps) {
  const initialParams = { email: '', message: '', phone: '' }
  const [params, setParams] = React.useState<IParams>(initialParams)
  const [validationHints, setValidationHints] = React.useState<Partial<IParams>>({})

  return (
    <div>
      <Text>
        Are you sure you want to be de-listed from our site? Parents are using Wonderschool to
        search for high quality child care. Learn more{' '}
        <Link to="/start" target="_blank">
          here
        </Link>
        .
      </Text>
      <div css={{ margin: `${spacing.majorScale(2)}px 0` }}>
        <Text>Confirm you would like to be removed from the Wonderschool website</Text>
      </div>
      <Form onSubmit={handleSubmit}>
        <FormSection>
          <TextField
            disabled
            label="Program name"
            placeholder="Program name"
            value={externalSchool.name}
            onChange={noop}
          />
          <FormGroup>
            <TextField
              label="Email"
              placeholder="Enter an email"
              validationHint={validationHints.email}
              value={params.email}
              onChange={email => handleChange({ email })}
              required
            />
            <PhoneTextField
              label="Phone number"
              placeholder="Phone number (mobile)"
              validationHint={validationHints.phone}
              value={params.phone}
              onChange={phone => handleChange({ phone })}
              required
            />
          </FormGroup>
          <TextArea
            placeholder="Message (optional)"
            value={params.message}
            onChange={message => handleChange({ message })}
          />
          <div css={{ marginTop: spacing.majorScale(4) }}>
            <Button full>Submit</Button>
          </div>
        </FormSection>
      </Form>
    </div>
  )

  function handleSubmit() {
    const hints = validateParams(params)
    setValidationHints(hints)

    if (Object.keys(hints).length > 0) return

    const data = {
      directorEmailGiven: params.email,
      directorEmailScraped: externalSchool.directorEmail,
      directorFirstName: externalSchool.directorFirstName,
      directorLastName: externalSchool.directorLastName,
      directorPhoneGiven: params.phone,
      directorPhoneScraped: externalSchool.directorPhone,
      externalSchoolLocation: formatAddress(externalSchool.address),
      message: params.message,
      recipient: SLACK_NOTIFICATION_CHANNEL,
    }

    segment.track('Click External School Opt-out Request Button', data)

    onComplete()
  }

  function handleChange(changes: Partial<IParams>) {
    setParams(currentParams => ({ ...currentParams, ...changes }))
  }
}

function validateParams(params: IParams) {
  const hints: Partial<IParams> = {}

  const email = params.email.trim()
  if (!email) {
    hints.email = 'Please enter an email'
  }

  if (email && !emailInputIsValid(email)) {
    hints.email = 'Please enter a valid email'
  }

  const phone = params.phone.trim()
  if (!phone) {
    hints.phone = 'Please enter a phone number'
  }

  if (phone && !phoneInputIsValid(phone)) {
    hints.phone = 'Please enter a valid phone number'
  }

  return hints
}

function noop() {
  /* noop */
}
